/**
 * @fololabs/design — brand tokens (the contract).
 *
 * Source canon: CGO/Output/FoloLabs_brand_assets_v1_2026-05-17.md.
 * These are framework-agnostic design tokens — colours, type stacks, and
 * the "other" tokens non-Mantine consumers (MapLibre overlays, charts,
 * raw CSS) reach for. `theme.ts` assembles them into the Mantine theme;
 * products may also import tokens directly.
 *
 * Stability: tokens are part of the public contract. Adding a token is a
 * MINOR bump; changing/removing a value or key is a MAJOR bump. See README.
 */

import type { MantineColorsTuple } from '@mantine/core';

/* ─── Palette — cool-mono v1 (10-shade tuples, brand-anchor noted) ──── */

/** Aquifer #0E2A47 — primary brand colour (anchored at index 7). */
export const aquifer: MantineColorsTuple = [
  '#E7ECF2',
  '#C5D1DD',
  '#9FB2C5',
  '#7993AD',
  '#527595',
  '#2B587D',
  '#174068',
  '#0E2A47', // ← brand Aquifer
  '#091F36',
  '#051528',
];

/** Spring #4E9DA6 — secondary accent (anchored at index 6). */
export const spring: MantineColorsTuple = [
  '#ECF6F7',
  '#CFE6E9',
  '#B0D5D9',
  '#91C3C9',
  '#72B1B8',
  '#5FA6AC',
  '#4E9DA6', // ← brand Spring
  '#3F8088',
  '#30636A',
  '#21464C',
];

/** Mist #F4F6F9 — light background; full grayscale tuple (anchored at index 2). */
export const mist: MantineColorsTuple = [
  '#FFFFFF',
  '#FBFCFD',
  '#F4F6F9', // ← brand Mist
  '#E5E9EE',
  '#CFD5DE',
  '#ADB6C2',
  '#8995A4',
  '#5E6B7C',
  '#3D4856',
  '#20272F',
];

/** Slate #0A1320 — text on light + dark-mode background (anchored at index 8). */
export const slate: MantineColorsTuple = [
  '#E4E7EC',
  '#C0C6CF',
  '#99A2AF',
  '#737E8E',
  '#4D5B6E',
  '#37475C',
  '#1F2C3E',
  '#131D2D',
  '#0A1320', // ← brand Slate
  '#040910',
];

/** Semantic status colours — read against Mist & Aquifer. Used sparingly. */
export const success: MantineColorsTuple = [
  '#ECF6EF', '#D0E7D7', '#A6D2B3', '#7BBC8E', '#52A66B',
  '#3A8E54', '#2D7644', '#235D36', '#1A4528', '#102C1A',
];
export const warning: MantineColorsTuple = [
  '#FBF3E4', '#F5E1B7', '#EECC83', '#E7B850', '#DBA52E',
  '#BC8E23', '#9E771A', '#7F5F13', '#60470C', '#412F06',
];
export const danger: MantineColorsTuple = [
  '#FAEAE8', '#F0C7C2', '#E3998F', '#D46B5D', '#C24D3C',
  '#A33B2C', '#84301F', '#662713', '#481C0B', '#2A1004',
];

/** All brand colour tuples, keyed for `MantineProvider` `colors`. */
export const colors = {
  aquifer,
  spring,
  mist,
  slate,
  success,
  warning,
  danger,
};

/* ─── Typography stacks (IBM Plex) ──────────────────────────────────── */

export const sansStack =
  '"IBM Plex Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
export const monoStack =
  '"IBM Plex Mono", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';
/** RTL: set `<html dir="rtl" lang="ar">` + apply this stack to the body. */
export const arabicFontStack =
  '"IBM Plex Sans Arabic", "IBM Plex Sans", "Segoe UI", sans-serif';

/* ─── "Other" tokens — for non-Mantine consumers ────────────────────── */

/** Map marker colours by data-quality tier (MapLibre overlays). */
export const mapMarkerColors = {
  tierA: '#0E2A47', // satellite-verified anchor
  tierB: '#4E9DA6', // structured registry
  tierC: '#8995A4', // metadata-only
  tierUnknown: '#CFD5DE',
};

export const dataQualityLegend = {
  A: 'Satellite-verified',
  B: 'Structured registry',
  C: 'Metadata only',
  U: 'Unverified',
};

/** Premium gradients (raw CSS, hero/overlay surfaces). */
export const gradients = {
  aquiferDeep:
    'linear-gradient(135deg, #0E2A47 0%, #1A4870 50%, #4E9DA6 100%)',
  springSoft:
    'linear-gradient(135deg, rgba(78,157,166,0.10) 0%, rgba(78,157,166,0.02) 100%)',
  heroOverlay:
    'linear-gradient(180deg, transparent 0%, rgba(10,19,32,0.7) 100%)',
};

export const transitions = {
  soft: '200ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  hover: '120ms ease-out',
};

/** Gold anchor accent — manager-own halo + 9-anchor-strip ring. */
export const goldAnchor = '#D4A574';

/**
 * Brand `other` bundle mounted on the Mantine theme — products read these
 * at render time via `useMantineTheme().other.*`.
 */
export const otherTokens = {
  mapMarkerColors,
  dataQualityLegend,
  arabicFontStack,
  gradients,
  transitions,
  goldAnchor,
};
